<script setup lang="ts">
// "How It Works" — mockup page 1, middle band.
//
// Three columns: the conversation (a phone frame showing the symptom chips
// the Ask HVAC page actually opens with), the four steps, and a sample
// assessment card. The assessment is explicitly labelled an example — it
// describes a made-up system, and a visitor must never mistake it for a
// reading of their own.
import { RouterLink } from 'vue-router'

const CHIPS = [
    'Not cooling',
    'Coil freezing',
    'Leaking water',
    "Won't turn on",
    'Weak airflow',
    'High electric bill',
    'House feels humid',
    'Making noise',
    'Something else',
]

const STEPS = [
    {
        title: 'Tell Us What’s Happening',
        body: 'Share a few details about your system.',
        icon: '<path d="M3 3.5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H8l-4 3v-3H3a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z"/>',
    },
    {
        title: 'Get Your Personalized Assessment',
        body: 'AI analyzes the likely problem.',
        icon: '<path d="M10 2a5.4 5.4 0 0 0-3.2 9.8V14a1 1 0 0 0 1 1h4.4a1 1 0 0 0 1-1v-2.2A5.4 5.4 0 0 0 10 2zm-2 14.4h4v1a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z"/>',
    },
    {
        title: 'Understand Your Options',
        body: 'Repair vs. replace and cost estimates.',
        icon: '<path d="M10 2.2v2.1M3.4 5.6h13.2M6.3 5.6 3.4 12h5.8zM13.7 5.6 10.8 12h5.8zM7.4 17.2h5.2v-1.4H7.4z"/><path d="M9.2 4.9h1.6v11.6H9.2z"/>',
    },
    {
        title: 'Take Action',
        body: 'Connect with an HVAC professional working with a licensed HVAC contractor.',
        icon: '<path d="M10 2.6 2.5 9h2v8h4v-5h3v5h4V9h2z"/>',
    },
]

// Illustrative only — see the label rendered above the card.
const EXAMPLE = [
    ['Likely issue', 'Refrigerant / airflow problem'],
    ['System age', '13 years'],
    ['Typical repair range', '$350 – $1,200'],
    ['Repair outlook', 'Consider replacement'],
    ['Why', 'Age + repair cost + efficiency'],
]
</script>

<template>
    <section class="how">
        <div class="inner">
            <!-- the conversation -->
            <div class="phone-col">
                <div class="phone" aria-hidden="true">
                    <div class="phone-top">
                        <span class="avatar"></span>
                        <span class="who">
                            <strong>Ask-HVAC Expert</strong>
                            <small>● Online</small>
                        </span>
                    </div>
                    <p class="bubble">What's going on with your AC?</p>
                    <div class="chips">
                        <span v-for="chip in CHIPS" :key="chip" class="chip">{{ chip }}</span>
                    </div>
                </div>
            </div>

            <!-- the steps -->
            <div class="steps-col">
                <h2>How It Works</h2>
                <p class="sub">Get Answers. Get Options. Make the Right <span class="hl">Choice.</span></p>

                <ol class="steps">
                    <li v-for="(step, i) in STEPS" :key="step.title">
                        <span class="bubble-icon">
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="step.icon"></svg>
                        </span>
                        <strong>{{ i + 1 }}. {{ step.title }}</strong>
                        <span class="body">{{ step.body }}</span>
                    </li>
                </ol>

                <p class="pill">✓ Fast &nbsp;•&nbsp; Easy &nbsp;•&nbsp; 100% Homeowner Focused</p>
            </div>

            <!-- the output -->
            <aside class="assess-col">
                <div class="assess">
                    <span class="assess-mark" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round">
                            <path d="M12 2v20M2 12h20M4.9 4.9l14.2 14.2M19.1 4.9 4.9 19.1" />
                        </svg>
                    </span>
                    <h3>Your AC Assessment</h3>
                    <p class="example-flag">Example — not a reading of your system</p>
                    <dl>
                        <div v-for="[k, v] in EXAMPLE" :key="k">
                            <dt>{{ k }}</dt>
                            <dd>{{ v }}</dd>
                        </div>
                    </dl>
                    <RouterLink class="cta" to="/second-opinion">Get a Free Second Opinion</RouterLink>
                    <p class="fine">Connect with a local HVAC pro</p>
                </div>
            </aside>
        </div>
    </section>
</template>

<style scoped lang="scss">
.how {
    background: var(--site-bg-alt);
    border-top: 1px solid var(--site-border);
    border-bottom: 1px solid var(--site-border);
}

.inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
    display: grid;
    grid-template-columns: 0.85fr 1.2fr 0.95fr;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 1040px) {
        grid-template-columns: 1fr;
        gap: var(--site-space-lg);
    }
}

h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3vw, 2.3rem);
    font-weight: 800;
    color: var(--site-navy);
    margin: 0;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    font-weight: 600;
    color: var(--site-text-muted);
}

/* --- phone --- */
.phone {
    background: var(--site-navy-deep);
    border-radius: 22px;
    padding: 0.9rem;
    border: 5px solid #10213f;
    box-shadow: var(--site-shadow-lg);
    color: #fff;
}

.phone-top {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);

    .avatar {
        width: 1.6rem;
        height: 1.6rem;
        border-radius: 50%;
        background: var(--site-blue);
    }

    .who {
        display: flex;
        flex-direction: column;
        line-height: 1.15;
    }

    strong {
        font-size: 0.8rem;
    }

    small {
        font-size: 0.62rem;
        color: #4ade80;
    }
}

.bubble {
    margin: 0.7rem 0;
    padding: 0.5rem 0.7rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    font-size: 0.8rem;
}

.chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.chip {
    padding: 0.3rem 0.55rem;
    border-radius: 999px;
    background: var(--site-blue);
    font-size: 0.68rem;
    font-weight: 600;
}

/* --- steps --- */
.steps {
    list-style: none;
    margin: 0 0 var(--site-space-lg);
    padding: 0;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--site-space-md);

    @media (max-width: 720px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    li {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 0.25rem;
    }

    strong {
        font-size: 0.82rem;
        color: var(--site-navy);
        line-height: 1.25;
    }

    .body {
        font-size: 0.72rem;
        color: var(--site-text-muted);
        line-height: 1.35;
    }
}

.bubble-icon {
    display: grid;
    place-items: center;
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 50%;
    background: var(--site-blue-soft);
    color: var(--site-blue);
    margin-bottom: 0.3rem;

    svg {
        width: 1.35rem;
        height: 1.35rem;
    }
}

.pill {
    display: inline-block;
    margin: 0;
    padding: 0.5rem 1.1rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-navy);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 600;
}

/* --- assessment --- */
.assess {
    background: var(--site-blue-soft);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-lg);
    text-align: center;
}

.assess-mark {
    display: inline-grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: #fff;
    color: var(--site-blue);
    margin-bottom: var(--site-space-sm);

    svg {
        width: 1.6rem;
        height: 1.6rem;
    }
}

h3 {
    font-family: var(--site-font-heading);
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--site-navy);
    margin: 0;
}

.example-flag {
    margin: 0.15rem 0 var(--site-space-md);
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--site-text-muted);

    /* This is the label that stops a sample assessment being mistaken for
       a real one. It is the last thing on the page that should be
       shrinking on the smallest screen. */
    @media (max-width: 860px) {
        font-size: 0.76rem;
    }
}

dl {
    margin: 0 0 var(--site-space-md);
    text-align: left;

    div {
        display: flex;
        gap: 0.4rem;
        padding: 0.28rem 0;
        border-bottom: 1px dashed rgba(29, 95, 191, 0.18);
        font-size: 0.76rem;
    }

    dt {
        color: var(--site-text-muted);
        flex-shrink: 0;

        &::before {
            content: '✓ ';
            color: var(--site-blue);
        }
    }

    dd {
        margin: 0 0 0 auto;
        font-weight: 600;
        color: var(--site-navy);
        text-align: right;
    }
}

.cta {
    display: block;
    padding: 0.6rem 1rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.88rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    /* 48px so a primary CTA is never the hardest thing on the page to
       hit. Mobile only — desktop keeps its tighter proportions. */
    @media (max-width: 860px) {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
    }
}

.fine {
    margin: 0.45rem 0 0;
    font-size: 0.7rem;
    color: var(--site-text-muted);
}
</style>
